
import { computed, defineComponent, ref } from 'vue'
import { useEpb } from '../../../composables/use-epb'
import { useResizableTextarea } from '../../../composables/use-resizable-textarea'
import EpbEditableInputLayout from '../epb-editable-input-layout.component.vue'

export default defineComponent({
  name: 'TitleInput',
  components: {
    EpbEditableInputLayout,
  },
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      required: true,
    },
    error: {
      type: String,
      default: undefined,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    input: (value: string | number) => typeof value === 'string' || typeof value === 'number',
    change: (value: string | number) => typeof value === 'string' || typeof value === 'number',
    keydown: (event: Event) => event instanceof Event,
    focus: () => true,
    blur: (event: FocusEvent) => event instanceof FocusEvent,
  },
  setup(props, { emit }) {
    const { $epb } = useEpb()

    const baseClass = 'epb-title-input'

    const messages = $epb.messages.app.inputs.titleInput

    const emptyError = $epb.utils.compileMessage(messages.emptyError, {
      label: props.label.toLowerCase(),
    })

    const textareaRef = ref<HTMLTextAreaElement>()
    const { resizeTextarea } = useResizableTextarea(
      textareaRef,
      computed(() => props.value),
    )

    const focused = ref(false)
    const hasBeenFocused = ref(false)

    const placeholder = props.required ? `${props.label} *` : props.label

    const handleInput = (event: Event) => {
      const target = event.target as HTMLTextAreaElement
      emit('input', target.value)
      resizeTextarea()
    }
    const handleFocus = () => {
      focused.value = true
      hasBeenFocused.value = true
      emit('focus')
    }

    const handleBlur = (event: FocusEvent) => {
      focused.value = false
      emit('blur', event)
    }

    const showEmptyError = computed(() => {
      return (
        !props.value && !focused.value && props.required && !props.disabled && hasBeenFocused.value
      )
    })

    return {
      baseClass,
      textareaRef,
      placeholder,
      focused,
      emptyError,
      handleInput,
      handleFocus,
      handleBlur,
      showEmptyError,
    }
  },
})
